import { useCallback } from 'react';
import { useUserProfile } from '../contexts/UserContext';
import { checkLimit, isExpired } from '../services/adminService';

/**
 * useSubscriptionGuard
 * 
 * Hook para verificar limites do plano antes de ações.
 * Super Admin nunca é bloqueado.
 * 
 * Uso:
 *   const { canPerform, guardAction, isBlocked, getUsageInfo } = useSubscriptionGuard();
 * 
 *   // Verificar antes de criar simulação:
 *   const check = canPerform('simulacoes', totalSimulacoes);
 *   if (!check.allowed) { alert(check.reason); return; }
 * 
 *   // Ou usar guardAction como wrapper:
 *   guardAction('clientes', totalClientes, async () => {
 *     await criarCliente(data);
 *   });
 */
export function useSubscriptionGuard() {
  const { subscription, isSuperAdmin } = useUserProfile();

  /**
   * Verifica se o plano está expirado/inativo
   */
  const isBlocked = useCallback(() => {
    if (isSuperAdmin) return false;
    if (!subscription) return true;
    return isExpired(subscription);
  }, [subscription, isSuperAdmin]);

  /**
   * Verifica se pode realizar ação baseado no recurso e uso atual
   * @param {'simulacoes'|'clientes'|'pdfs'|'usuarios'} resource
   * @param {number} currentCount - Quantidade atual usada
   * @returns {{ allowed: boolean, reason?: string }}
   */
  const canPerform = useCallback((resource, currentCount) => {
    if (isSuperAdmin) return { allowed: true };

    if (!subscription) {
      return { allowed: false, reason: 'Você não possui um plano ativo. Escolha um plano para continuar.' };
    }

    if (isExpired(subscription)) {
      return { allowed: false, reason: 'Seu plano expirou. Renove para continuar usando o sistema.' };
    }

    return checkLimit(subscription, resource, currentCount);
  }, [subscription, isSuperAdmin]);

  /**
   * Wrapper que executa ação somente se permitido
   * @param {'simulacoes'|'clientes'|'pdfs'|'usuarios'} resource
   * @param {number} currentCount
   * @param {Function} action - Função a executar se permitido
   * @param {Function} [onBlocked] - Callback se bloqueado (recebe reason)
   */
  const guardAction = useCallback(async (resource, currentCount, action, onBlocked) => {
    const check = canPerform(resource, currentCount);
    if (!check.allowed) {
      if (onBlocked) {
        onBlocked(check.reason);
      } else {
        // Default: alert
        alert(check.reason);
      }
      return false;
    }
    await action();
    return true;
  }, [canPerform]);

  /**
   * Retorna informações de uso do recurso
   * @param {'simulacoes'|'clientes'|'pdfs'|'usuarios'} resource
   * @param {number} currentCount
   * @returns {{ used: number, limit: number, unlimited: boolean, percentage: number }}
   */
  const getUsageInfo = useCallback((resource, currentCount) => {
    if (isSuperAdmin) {
      return { used: currentCount, limit: -1, unlimited: true, percentage: 0 };
    }

    if (!subscription || !subscription.limites) {
      return { used: currentCount, limit: 0, unlimited: false, percentage: 100 };
    }

    const limit = subscription.limites[resource];
    const unlimited = limit === -1;
    const percentage = unlimited ? 0 : limit > 0 ? Math.round((currentCount / limit) * 100) : 100;

    return { used: currentCount, limit, unlimited, percentage };
  }, [subscription, isSuperAdmin]);

  return {
    canPerform,
    guardAction,
    isBlocked,
    getUsageInfo,
    subscription,
    isSuperAdmin,
  };
}
